/* eslint-disable @typescript-eslint/indent */
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import {
  GetApplicationsQueryResult,
  SimulatorResultItem,
} from '@sdir/httpclient/lib/clients/aps/application';
import { Loader, styles } from '@sdir/sds';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { CardRow, Card, InputError } from '../../Atoms';
import {
  qualificationSetIsComplete,
  findQualificationAttachment,
  qualificationIsComplete,
} from '../../../helpers/Qualifications';
import { useCurrentApplication, useAddedQualifications } from '../../../services/hooks';
import { AddedQualifications } from '../../../types';

const QualificationSummaryCard: React.FC<{
  title: string;
  hideCompleted?: boolean;
}> = ({ title, hideCompleted = false }) => {
  const intl = useIntl();
  const { application, qualifications } = useCurrentApplication({ includeQualifications: true });
  const { addedSeagoing, addedNewSeagoing } = useAddedQualifications({ all: true });

  const addedQualificationsLoading = addedSeagoing.loading;
  const addedQualificationsError = addedSeagoing.error;

  const addedQualifications: AddedQualifications = useMemo(
    () => ({
      seagoing: addedSeagoing.result || [],
      newSeagoing: addedNewSeagoing.result || {},
    }),
    [addedSeagoing.result, addedNewSeagoing.result]
  );
  const validAlternativeIndexes = useMemo(() => {
    const valid: number[] = [];
    qualifications.result?.alternatives?.forEach((alternative, index) => {
      const isComplete = qualificationSetIsComplete(
        alternative,
        addedQualifications,
        application.result?.attachments || [],
        application.result?.confirmedQualifications || []
      );
      isComplete && valid.push(index);
    });
    return valid;
  }, [addedQualifications, application.result?.attachments, qualifications.result?.alternatives]);

  return (
    <QualificationCard heading={title}>
      {(application.loading || qualifications.loading || addedQualificationsLoading) && <Loader />}
      {(application.error || qualifications.error || addedQualificationsError) && (
        <ErrorWrapper>
          <ErrorMessage
            error={application.error || qualifications.error || addedQualificationsError}
          />
        </ErrorWrapper>
      )}
      {qualifications.result && application.result && !addedQualificationsLoading && (
        <>
          <QualificationCardRow
            label={intl.formatMessage({
              id: 'newapp.steps.qualifications',
            })}
          >
            <QualificationList
              items={qualifications.result?.base?.items || []}
              application={application.result}
              hideCompleted={hideCompleted}
              addedQualifications={addedQualifications}
            />
          </QualificationCardRow>
          {validAlternativeIndexes.length
            ? validAlternativeIndexes.map((i) => {
                const alternative = qualifications.result?.alternatives?.[i];
                return (
                  <QualificationCardRow
                    key={i}
                    label={intl.formatMessage(
                      { id: 'qualificationsummary.additionalalternative' },
                      { number: (i || 0) + 1 }
                    )}
                  >
                    <QualificationList
                      items={alternative?.items || []}
                      application={application.result}
                      hideCompleted={hideCompleted}
                      addedQualifications={addedQualifications}
                    />
                  </QualificationCardRow>
                );
              })
            : qualifications.result?.alternatives?.map((alt, i) => (
                <QualificationCardRow
                  key={i}
                  label={intl.formatMessage(
                    { id: 'qualificationsummary.additionalalternative' },
                    { number: i + 1 }
                  )}
                >
                  <QualificationList
                    items={alt?.items || []}
                    application={application.result}
                    hideCompleted={hideCompleted}
                    addedQualifications={addedQualifications}
                  />
                </QualificationCardRow>
              ))}
        </>
      )}
    </QualificationCard>
  );
};

const QualificationList: React.FC<{
  application: GetApplicationsQueryResult;
  items: SimulatorResultItem[];
  addedQualifications: AddedQualifications;
  hideCompleted?: boolean;
}> = ({ items, application, hideCompleted, addedQualifications }) => {
  const filteredItems = useMemo(() => {
    let currentItems = items;
    if (hideCompleted) {
      currentItems = currentItems.filter(
        // include if not valid, or has added info/attachment
        (i) => !i.status || !!findQualificationAttachment(i, application.attachments || [])
      );
    }
    return currentItems;
  }, [application.attachments, hideCompleted, items]);
  return (
    <div>
      {filteredItems.map((x) => (
        <QualificationItem
          key={x.uri}
          qualification={x}
          application={application}
          addedQualifications={addedQualifications}
        />
      ))}
    </div>
  );
};

const QualificationItem: React.FC<{
  application: GetApplicationsQueryResult;
  qualification: SimulatorResultItem;
  addedQualifications: AddedQualifications;
}> = ({ qualification, application, addedQualifications }) => {
  const intl = useIntl();

  const invalid = useMemo(() => {
    return !qualificationIsComplete(
      qualification,
      addedQualifications,
      application.attachments || [],
      application.confirmedQualifications || []
    );
  }, [addedQualifications, application.attachments, qualification]);

  return (
    <QualificationItemWrapper invalid={invalid}>
      <div className="qualification-item">
        <div>
          <span className="qualification-item__title">{qualification.title}</span>:
          <div dangerouslySetInnerHTML={{ __html: qualification.description || '' }} />
        </div>
      </div>
      {invalid && (
        <InputError>
          {intl.formatMessage({ id: 'qualificationsummary.error.incomplete' })}
        </InputError>
      )}
    </QualificationItemWrapper>
  );
};

const QualificationItemWrapper = styled.div<{ invalid: boolean }>`
  margin-bottom: 8px;

  ${InputError} {
    padding-right: 32px;
    padding-left: 32px;
  }

  .qualification-item {
    padding: 0 32px;

    span,
    div {
      font-family: ${styles.fonts.stfBook};

      & > div {
        display: inline;
        margin-left: 5px;
      }
    }

    .addedInfoLink {
      display: inline;
    }

    .qualification-item__title {
      font-family: ${styles.fonts.stfMedium};
    }

    ${({ invalid, theme }) =>
      invalid &&
      css`
        margin-top: -2px;
        background: ${theme.colors.alert.errorLight};
        border-top: 2px solid ${theme.colors.alert.errorDark};
        border-bottom: 2px solid ${theme.colors.alert.errorDark};
        padding: 16px 32px;
      `}
  }
`;

const QualificationCard = styled(Card)`
  &&& {
    padding-left: 0;
    padding-right: 0;

    .card__heading {
      padding: 0 32px;
    }
  }
`;

const QualificationCardRow = styled(CardRow)`
  &&& {
    padding-left: 0;
    padding-right: 0;

    .cardValue__label {
      padding: 0 32px;
    }
  }
`;

const ErrorWrapper = styled.div`
  margin: 0 32px;
`;

export default QualificationSummaryCard;
